import { useMemo, useState } from 'react';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import Card from 'components/insights/layout/Card';
import AcceptReferralModal from 'components/modals/referral/AcceptReferralModal';
import DenyReferralModal from 'components/modals/referral/DenyReferralModal';
import ReferralModal from 'components/modals/referral/SendReferralModal';
import Button from 'components/shared/Button';
import PermissionGuard from 'components/shared/PermissionGuard';
import { dateWithoutTimezone } from 'lib/date';
import Episode from 'models/Episode';
import Group from 'models/Group';
import LocationEpisode from 'models/LocationEpisode';
import { PERMISSIONS } from 'models/Profile';
import Profile from 'models/Profile';
import Referral from 'models/reviews/Referral';
import colors from 'styles/theme/colors';
import { H3, LabelBold } from 'styles/typography';
import ReferralIcon from 'svg/ReferralIcon';
import ReferralIconOutlined from 'svg/ReferralsIconOutlined';

import { ReferralMenu } from './ReferralMenu';

const getReferralBannerValues = (review: Referral, referredBy?: Group, isReferrer: boolean = false) => {
  const { createdAt, group, createdBy } = review;

  const headerText =
    referredBy && !isReferrer ? `Referral received from ${referredBy.name}` : `Referral sent to ${group?.name}`;
  const boldedText = (
    <b>
      Referral{' '}
      {referredBy && !isReferrer
        ? `from ${referredBy.name} was sent by ${createdBy?.fullName}`
        : `sent to ${group?.name}`}
      {' on '}
      {dateWithoutTimezone(createdAt)}.
    </b>
  );

  if (review.pending) {
    return {
      headerText,
      bodyText: (
        <>
          {boldedText}{' '}
          {referredBy && !isReferrer
            ? 'View more details and add notes'
            : `Coordinate with providers at ${group?.name} around referral needs`}{' '}
          below.
        </>
      ),
      statusColors: { backgroundColor: colors.black10, color: colors.black75 },
    };
  }
  if (review.accepted) {
    return {
      headerText,
      bodyText: (
        <>
          {boldedText}{' '}
          {referredBy && !isReferrer
            ? 'View more details and add notes'
            : `Coordinate with providers at ${group?.name} around referral needs`}{' '}
          below.
        </>
      ),
      statusColors: { backgroundColor: colors.actionGreen, color: colors.white },
    };
  }
  if (review.rejected) {
    return {
      headerText,
      bodyText: <>{boldedText} View more details below.</>,
      statusColors: { backgroundColor: colors.accentRed, color: colors.white },
    };
  }
  if (review.canceled) {
    return {
      headerText,
      bodyText: <>{boldedText} View more details below.</>,
      statusColors: { backgroundColor: colors.black75, color: colors.white },
    };
  }
};

export type ReferralBannerType = {
  review?: Referral;
  profile?: Profile;
  episode?: Episode;
  locationEpisode?: LocationEpisode;
};

export function ReferralBanner({ review, profile, locationEpisode, episode }: ReferralBannerType) {
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);

  const referredBy = useMemo(() => locationEpisode?.group, [locationEpisode]);

  const isReferrer = useMemo(() => {
    return (
      episode && locationEpisode && (profile?.isManagerFor(episode) || profile?.isProviderFor(locationEpisode?.groupId))
    );
  }, [episode, locationEpisode, profile]);

  const handleReferPatientClick = () => {
    setShowReferralModal(true);
  };

  if (!review) {
    return (
      episode &&
      locationEpisode && (
        <>
          <StyledCard>
            <IconContainer>
              <ReferralIconOutlined />
            </IconContainer>
            <NoReferralTitle>Refer Patient</NoReferralTitle>
            <NoReferralText>
              Send a referral to a provider in Olio to begin coordinating care and exchanging necessary documentation
            </NoReferralText>
            <ReferButton onClick={handleReferPatientClick}>Refer Patient</ReferButton>
          </StyledCard>

          {showReferralModal && (
            <ReferralModal
              onCancel={() => setShowReferralModal(false)}
              onSuccess={() => setShowReferralModal(false)}
              locationEpisode={locationEpisode}
              episode={episode}
              patientName={`${episode?.patient.name}`}
            />
          )}
        </>
      )
    );
  }

  const { headerText, bodyText, statusColors } = getReferralBannerValues(review, referredBy, isReferrer) || {};

  const showAcceptDenyButtons =
    referredBy && review?.groupId && review?.pending && profile?.canAccessGroup(review.groupId);

  return (
    <>
      <Container data-cy='referral-banner'>
        <Header>
          <ReferralIcon />
          <LabelBold>{headerText}</LabelBold>
          {!!review && !!locationEpisode && !!episode && (
            <ReferralMenu referral={review} locationEpisode={locationEpisode} episode={episode} />
          )}
        </Header>
        <BodyContainer>
          <BodyText>{bodyText}</BodyText>
          {showAcceptDenyButtons ? (
            <>
              <PermissionGuard
                permission={PERMISSIONS.reviewReferralEdit}
                locationEpisode={locationEpisode!}
                episode={episode!}>
                <StatusContainer>
                  <ResponseButton variant='call-to-action' size='xsmall' onClick={() => setShowAcceptModal(true)}>
                    Accept
                  </ResponseButton>
                  <ResponseButton variant='danger' size='xsmall' onClick={() => setShowDenyModal(true)}>
                    Deny
                  </ResponseButton>
                </StatusContainer>
              </PermissionGuard>
            </>
          ) : (
            statusColors && (
              <StatusContainer>
                <Status {...statusColors}>{review?.rejected ? 'Denied' : capitalize(review?.status)}</Status>
              </StatusContainer>
            )
          )}
        </BodyContainer>
      </Container>
      {showAcceptModal && episode && review && (
        <AcceptReferralModal
          handleClose={() => setShowAcceptModal(false)}
          patientName={episode.patient.name}
          referredBy={referredBy!}
          locationEpisode={locationEpisode!}
          review={review}
        />
      )}
      {showDenyModal && episode && review && (
        <DenyReferralModal
          handleClose={() => setShowDenyModal(false)}
          patientName={episode.patient.name}
          locationEpisode={locationEpisode!}
          review={review}
        />
      )}
    </>
  );
}

const Container = styled.div`
  background-color: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
`;

const BodyContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-direction: column;
  @media ${({ theme }) => theme.devices.desktop} {
    flex-direction: row;
  }
`;

const BodyText = styled.span`
  margin: 24px;
  font-size: 12px;
  line-height: 20px;
  color: ${colors.black};
  @media ${({ theme }) => theme.devices.desktop} {
    margin-right: auto;
  }
`;

const Header = styled.div`
  align-items: center;
  background-color: ${colors.primaryBlue};
  color: ${colors.white};
  display: flex;
  gap: 16px;
  justify-content: start;
  padding: 8px 24px;
  border-radius: 4px 4px 0px 0px;
`;

const Status = styled(LabelBold)<{ backgroundColor: string; color: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ color }) => color};
  border-radius: 16px;
  margin-left: 0px;
  padding: 4px 8px;
`;

const StatusContainer = styled.div`
  display: flex;
  align-self: stretch;
  align-items: center;
  gap: 8px;
  margin: 0 24px 24px;
  @media ${({ theme }) => theme.devices.desktop} {
    padding-left: 24px;
    border-left: 1px solid ${colors.black10};
    margin: 16px 24px 16px 24px;
  }
`;

const ResponseButton = styled(Button)`
  width: 80px;
  height: 32px;
  flex: 1;
`;

const StyledCard = styled(Card)`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
  text-align: center;
`;

const NoReferralTitle = styled(H3)`
  font-weight: var(--font-weight-bold);
`;

const NoReferralText = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.black75};
`;

const IconContainer = styled.div`
  color: var(--primary-blue);
`;

const ReferButton = styled(Button)`
  margin-top: 8px;
`;
